import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Pencil, UserCheck, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useDirectory } from "@/hooks/useApi";
import { PageHeader, Section, KV, StatusBadge, Loading, ErrorBox } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CommentsBox } from "@/components/common/CommentsBox";
import { leadFields } from "@/lib/schemas";
import { fmtDate, fmtDateTime } from "@/lib/format";

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: lead, error, reload } = useApi(`/leads/${id}`);
  const { options: staff, name } = useDirectory();
  const [edit, setEdit] = useState(false);
  const [follow, setFollow] = useState(false);
  const [convert, setConvert] = useState(false);
  if (error) return <ErrorBox text={error} />;
  if (!lead) return <Loading />;

  return (
    <div>
      <PageHeader back="/prospectos" title={lead.name} subtitle={lead.company}
        actions={<>
          <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-lead-button"><Pencil className="mr-2 h-4 w-4" />Editar</Button>
          <Button variant="outline" onClick={() => setFollow(true)} data-testid="schedule-followup-button"><CalendarPlus className="mr-2 h-4 w-4" />Programar seguimiento</Button>
          {lead.client_id ? <Button onClick={() => navigate(`/clientes/${lead.client_id}`)} data-testid="view-converted-client-button">Ver cliente</Button>
            : <Button onClick={() => setConvert(true)} data-testid="convert-lead-button"><UserCheck className="mr-2 h-4 w-4" />Convertir en cliente</Button>}
        </>} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Section title="Información" className="lg:col-span-2">
          <KV items={[["Estado", <StatusBadge key="s" status={lead.status} testid="lead-status" />], ["Correo", lead.email], ["Teléfono", lead.phone],
            ["Ciudad", lead.city], ["Estado / Provincia", lead.state], ["País", lead.country], ["Tipo de cliente", lead.client_type],
            ["Servicio de interés", lead.service_interest], ["Fuente", lead.source], ["Fecha de contacto", fmtDate(lead.contact_date)],
            ["Próximo seguimiento", fmtDate(lead.next_followup)], ["Responsable", name(lead.responsible_id)]]} />
        </Section>
        <Section title="Seguimientos programados">
          <ul className="space-y-2 text-sm" data-testid="followups-list">
            {(lead.followups || []).length === 0 && <li className="text-slate-400">Sin seguimientos.</li>}
            {(lead.followups || []).slice().reverse().map((f) => (
              <li key={f.id} className="rounded-md border border-slate-200 p-2"><b>{fmtDate(f.date)}</b> — {f.note || "Sin nota"}<span className="block text-xs text-slate-400">{f.by_name} · {fmtDateTime(f.at)}</span></li>
            ))}
          </ul>
        </Section>
        <Section title="Notas" className="lg:col-span-3">
          <CommentsBox testid="lead-notes" comments={lead.notes} placeholder="Agregar nota…"
            onAdd={async (text) => { await api.post(`/leads/${id}/notes`, { text }); reload(); }} />
        </Section>
      </div>
      <FormDialog open={edit} onOpenChange={setEdit} title="Editar prospecto" testid="lead-edit-form" fields={leadFields(staff)} initial={lead}
        onSubmit={async (v) => { await api.patch(`/leads/${id}`, v); toast.success("Prospecto actualizado"); reload(); }} />
      <FormDialog open={follow} onOpenChange={setFollow} title="Programar seguimiento" testid="followup-form" submitLabel="Programar"
        fields={[{ name: "date", label: "Fecha", type: "date", required: true }, { name: "note", label: "Nota", type: "textarea" }]}
        onSubmit={async (v) => { await api.post(`/leads/${id}/followups`, v); toast.success("Seguimiento programado"); reload(); }} />
      <ConfirmDialog open={convert} onOpenChange={setConvert} title="Convertir en cliente" testid="convert-lead-confirm" confirmLabel="Convertir"
        description="Se creará un cliente con los datos de este prospecto (nombre, empresa, contacto, ubicación y notas). El prospecto quedará como Ganado."
        onConfirm={async () => {
          try { const r = await api.post(`/leads/${id}/convert`); toast.success("Cliente creado"); navigate(`/clientes/${r.data.id}`); }
          catch (e) { toast.error(errMsg(e)); }
        }} />
    </div>
  );
}
